import { useCallback, useState } from 'react';
import type {
  TrophyProduct,
  TrophyQuote,
  TrophyActiveTab,
} from '../types/trophies';
import * as trophiesService from '../services/trophies.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseTrophiesProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  externalActiveTab?: TrophyActiveTab;
}

export function useTrophies({ accessToken, refreshToken, attemptRefresh, externalActiveTab }: UseTrophiesProps) {
  // Active tab (use external if provided, otherwise internal)
  const [internalActiveTab, setInternalActiveTab] = useState<TrophyActiveTab>('products');
  const activeTab = externalActiveTab ?? internalActiveTab;
  const setActiveTab = setInternalActiveTab;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [products, setProducts] = useState<TrophyProduct[]>([]);
  const [quotes, setQuotes] = useState<TrophyQuote[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<unknown>(null);

  // ===== Fetch Functions =====

  const fetchProducts = useCallback(async () => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => trophiesService.fetchProducts(accessToken!, { search: searchTerm, includeInactive }),
        refreshToken,
        attemptRefresh
      );
      setProducts(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar produtos' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, searchTerm, includeInactive]);

  const fetchQuotes = useCallback(async (params?: { search?: string; status?: string }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setLoading(true);
    try {
      const data = await withAuthRetry(
        () => trophiesService.fetchQuotes(accessToken!, params),
        refreshToken,
        attemptRefresh
      );
      setQuotes(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao buscar orçamentos' };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh]);

  // ===== Fetch All Based on Active Tab =====

  const fetchData = useCallback(async () => {
    switch (activeTab) {
      case 'products':
        return fetchProducts();
      case 'quotes':
        await fetchProducts();
        return fetchQuotes();
      default:
        return { success: true };
    }
  }, [activeTab, fetchProducts, fetchQuotes]);

  // ===== CRUD Operations =====

  const createProduct = useCallback(async (data: Parameters<typeof trophiesService.createProduct>[0]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      const product = await trophiesService.createProduct(data, accessToken);
      await fetchProducts();
      return { success: true, data: product };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao criar produto' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchProducts]);

  const updateProduct = useCallback(async (id: string, data: Parameters<typeof trophiesService.updateProduct>[1]) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    setSaving(true);
    try {
      const product = await trophiesService.updateProduct(id, data, accessToken);
      await fetchProducts();
      return { success: true, data: product };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar produto' };
    } finally {
      setSaving(false);
    }
  }, [accessToken, fetchProducts]);

  const deleteProduct = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await trophiesService.deleteProduct(id, accessToken);
      await fetchProducts();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao excluir produto' };
    }
  }, [accessToken, fetchProducts]);

  const toggleProductStatus = useCallback(async (id: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await trophiesService.toggleProductStatus(id, accessToken);
      await fetchProducts();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao alternar status' };
    }
  }, [accessToken, fetchProducts]);

  const updateQuoteStatus = useCallback(async (id: string, status: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await trophiesService.updateQuoteStatus(id, status, accessToken);
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar status' };
    }
  }, [accessToken, fetchQuotes]);

  const markTrophyQuoteAsViewed = useCallback(async (quoteId: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    // Optimistically update local state
    setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, isViewed: true } : q
      )
    );

    try {
      await withAuthRetry(
        () => trophiesService.markTrophyQuoteAsViewed(quoteId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      // Revert optimistic update on error
      setQuotes((prev) =>
        prev.map((q) =>
          q.id === quoteId ? { ...q, isViewed: false } : q
        )
      );
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao marcar como visualizado' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const updateQuoteItem = useCallback(async (itemId: string, data: { quantity: number }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const result = await withAuthRetry(
        () => trophiesService.updateQuoteItem(itemId, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao atualizar item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const deleteQuoteItem = useCallback(async (itemId: string) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      await withAuthRetry(
        () => trophiesService.deleteQuoteItem(itemId, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao remover item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  const addQuoteItem = useCallback(async (quoteId: string, data: { productId: string; quantity: number; dimensionId?: string }) => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };
    try {
      const result = await withAuthRetry(
        () => trophiesService.addQuoteItem(quoteId, data, accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchQuotes();
      return { success: true, data: result };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Erro ao adicionar item' };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchQuotes]);

  return {
    // Tab
    activeTab,
    setActiveTab,

    // Loading
    loading,
    saving,

    // Data
    products,
    quotes,
    setQuotes,

    // Filters
    searchTerm,
    setSearchTerm,
    includeInactive,
    setIncludeInactive,

    // Dialog
    dialogOpen,
    setDialogOpen,
    selectedItem,
    setSelectedItem,

    // Fetch
    fetchData,
    fetchProducts,
    fetchQuotes,

    // CRUD
    createProduct,
    updateProduct,
    deleteProduct,
    toggleProductStatus,
    updateQuoteStatus,
    markTrophyQuoteAsViewed,
    updateQuoteItem,
    deleteQuoteItem,
    addQuoteItem,
  };
}
